import React, { useState } from 'react';
import { BadgeCheck, Mail, Phone, UserRound } from 'lucide-react';

// The owner's advisor on their team (Succeedo Phase 5, PRD §5-1, §5-2).
//
// - Referred by an advisor (or claimed after asking): "Your advisor", pinned
//   above the seats, with how to reach them and a way to remove them.
// - From an enterprise page, no advisor yet: an offer to ask for one, with
//   exactly what the advisor would see.
// - Asked, waiting: who is being asked and that nothing more is shared.
// Renders nothing without a sponsorship (the page is as before).
export default function AdvisorCard({ sponsorship, onRequestAdvisor, onRemoveAdvisor, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  if (!sponsorship) return null;

  const { advisor, enterprise, status } = sponsorship;
  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      setConfirmRemove(false);
    } catch (e) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const shell = 'bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px]';

  if (advisor) {
    return (
      <section className={`${shell} ${compact ? 'p-4' : 'p-5'}`} data-testid="advisor-card" aria-label="Your advisor">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[hsl(var(--succeedo-orange))]/15 text-[hsl(var(--succeedo-orange))] flex items-center justify-center shrink-0">
              <UserRound className="w-5 h-5" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your advisor</span>
              <span className="text-[15px] font-semibold text-foreground">{advisor.name}</span>
              <span className="text-[13px] text-muted-foreground">
                {[advisor.title, advisor.firm || advisor.enterprise].filter(Boolean).join(' · ')}
              </span>
              {!compact && (
                <span className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[13px]">
                  {advisor.email && (
                    <a href={`mailto:${advisor.email}`} className="inline-flex items-center gap-1 text-foreground">
                      <Mail className="w-3.5 h-3.5" /> {advisor.email}
                    </a>
                  )}
                  {advisor.phone && (
                    <a href={`tel:${advisor.phone}`} className="inline-flex items-center gap-1 text-foreground">
                      <Phone className="w-3.5 h-3.5" /> {advisor.phone}
                    </a>
                  )}
                </span>
              )}
            </div>
          </div>
          {!compact && onRemoveAdvisor && (
            confirmRemove ? (
              <span className="flex items-center gap-2 text-[13px]">
                <span className="text-muted-foreground">They'll stop seeing your progress and briefs.</span>
                <button type="button" disabled={busy} onClick={() => run(onRemoveAdvisor)} className="text-coral-ink font-medium" data-testid="advisor-remove-confirm">
                  Remove
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmRemove(false)} className="text-muted-foreground">
                  Keep
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)} className="text-[13px] text-muted-foreground hover:text-foreground" data-testid="advisor-remove">
                Remove from my team
              </button>
            )
          )}
        </div>
        {!compact && (
          <p className="m-0 mt-3 text-[12px] text-muted-foreground flex items-start gap-1.5">
            <BadgeCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {advisor.name} sees that you joined and how far you've got. They see what you work on only when you send
            them a brief, and never your conversations with George.
          </p>
        )}
        {error && <p className="m-0 mt-2 text-[12px] text-coral-ink">{error}</p>}
      </section>
    );
  }

  if (status === 'routing') {
    return (
      <section className={`${shell} p-5`} data-testid="advisor-requested">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your advisor</span>
        <p className="m-0 mt-1 text-[14px] text-foreground">
          We've asked a {enterprise} advisor to get in touch. Once they accept, they'll appear here.
        </p>
        <p className="m-0 mt-1 text-[12px] text-muted-foreground">They see your name, email and state, nothing else.</p>
      </section>
    );
  }

  if (status === 'unassigned') {
    return (
      <section className={`${shell} p-5`} data-testid="advisor-unassigned">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Your advisor</span>
        <p className="m-0 mt-1 text-[14px] text-foreground">{enterprise} is finding the right advisor for you.</p>
      </section>
    );
  }

  if (sponsorship.can_request_advisor && onRequestAdvisor) {
    return (
      <section className={`${shell} p-5 flex items-start justify-between gap-4 flex-wrap`} data-testid="advisor-offer">
        <div className="flex flex-col gap-1 max-w-xl">
          <span className="text-[15px] font-semibold text-foreground">Want a {enterprise} advisor on your team?</span>
          <span className="text-[13px] text-muted-foreground">
            An advisor who works with business owners can help with your personal plan: retirement, estate, insurance
            and what the sale means for you. They'll see your name, email and state, nothing else unless you send them a brief.
          </span>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(onRequestAdvisor)}
          className="shrink-0 rounded-full px-5 py-2 text-[14px] font-medium bg-[hsl(var(--succeedo-orange))] text-white disabled:opacity-60"
          data-testid="advisor-request"
        >
          Ask for an advisor
        </button>
        {error && <p className="m-0 w-full text-[12px] text-coral-ink">{error}</p>}
      </section>
    );
  }

  return null;
}
